import React, { useEffect, useRef, useState } from "react";
import OrbitalLoader from "../common/OrbitalLoader";
import { brandsService } from "../../services/brands";

const CheckIcon = () => (
  <svg
    className="w-4 h-4 text-green-400 shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2.5}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const StepSpinner = ({ active = false }) => (
  <span className="inline-flex items-center justify-center w-4 h-4 shrink-0 overflow-visible">
    <OrbitalLoader size={32} special={active} />
  </span>
);

const StepDot = () => (
  <span className="inline-block w-4 h-4 rounded-full border-2 border-theme-secondary shrink-0" />
);

export function IdeaGenerationProgress({
  runHash,
  onContinueClick,
  continueLabel = "View new ideas",
}) {
  const [data, setData] = useState(null);
  const timeoutRef = useRef(null);

  const POLL_INTERVAL_MS = 3000;

  useEffect(() => {
    if (!runHash) return undefined;

    let cancelled = false;

    const pollStatus = () => {
      brandsService.getIdeaRunPopulationStatus(
        runHash,
        (response) => {
          if (cancelled) return;
          const payload = response?.data ?? response;
          setData(payload);

          const done =
            payload?.status === "completed" || payload?.status === "failed";
          if (!done) {
            timeoutRef.current = setTimeout(pollStatus, POLL_INTERVAL_MS);
          }
        },
        () => {
          if (cancelled) return;
          setData((prev) => prev);
        },
      );
    };

    pollStatus();

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
    };
  }, [runHash]);

  const steps = data?.steps || [];
  const isCompleted = data?.status === "completed";
  const hasFailed = data?.status === "failed";
  const isFinished = isCompleted || hasFailed;

  const findActiveIdx = () => {
    for (let i = 0; i < steps.length; i += 1) {
      if (!steps[i].done) return i;
    }
    return -1;
  };
  const activeIdx = findActiveIdx();

  return (
    <>
      <h2 className="text-2xl font-semibold text-theme-text mb-1">
        {isCompleted
          ? "Ideas Ready"
          : hasFailed
            ? "Generation Failed"
            : "Generating Ideas"}
      </h2>
      <p className="text-sm text-theme-text opacity-60 mb-6">
        {isCompleted
          ? `${data?.created_count ?? 0} new idea${data?.created_count === 1 ? "" : "s"} added to your queue.`
          : hasFailed
            ? data?.status_error || "Something went wrong. You can try again from the ideas page."
            : "This usually takes 30-90 seconds."}
      </p>

      <div className="space-y-3 mb-8">
        {steps.map((step, idx) => {
          const isActive = idx === activeIdx && !isFinished;
          return (
            <div
              key={step.key}
              className={`flex items-center gap-3 text-sm ${
                step.done
                  ? "text-theme-text"
                  : isActive
                    ? "text-theme-text"
                    : "text-theme-text opacity-40"
              }`}
            >
              {step.done ? (
                <CheckIcon />
              ) : isActive ? (
                <StepSpinner active />
              ) : (
                <StepDot />
              )}
              <span className="flex-1">{step.label}</span>
            </div>
          );
        })}
        {!data && (
          <div className="flex items-center gap-3 text-sm text-theme-text">
            <StepSpinner active />
            <span>Initializing...</span>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        {isFinished ? (
          <button
            type="button"
            onClick={onContinueClick}
            className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90"
          >
            {continueLabel}
          </button>
        ) : (
          <span className="px-4 py-2 bg-theme-primary text-theme-button rounded-md opacity-50 cursor-not-allowed">
            {continueLabel}
          </span>
        )}
      </div>
    </>
  );
}
